import ForecastSummaryValue from "./ForecastSummaryValue.js";
import Model from "../../Model.js";

/**
 * `ForecastSeriesComputationRequest` makes it easy to build a request
 * to compute forecast series. The API presents a fluent interface so
 * messages can be chained, each public method returning the receiver.
 *
 * @param {Forecast} forecast - The object containing the data to
 *   compute
 * @param {string[]} ignoredReportIds - List of forecast report ids
 *   that shouldn't be computed (e.g., `["income-statement"]`)
 */
export default class ForecastSeriesComputationRequest extends Model {
	constructor({ forecast, description = "", ignoredReportIds } = {}) {
		super(...arguments);

		this._forecast = forecast;

		this._seriesSpecifications = [];
		this._rules = [];
		this._referencePeriods = this._makeReferencePeriods();
		this._forecastPeriods = this._makeForecastPeriods();
		this._summaryValues = this._makeSummaryValues();
		this._description = description;
		this._ignoredReportIds = ignoredReportIds;
	}

	addRuleTemplates(ruleTemplates = []) {
		ruleTemplates.forEach((ruleTemplate) =>
			this.addRuleTemplate(ruleTemplate),
		);

		return this;
	}

	addRuleTemplate(ruleTemplate) {
		const stableRowId = ruleTemplate.getRowId();
		const row = this._forecast.getRowByStableId(stableRowId);
		const accountSpecification = row.asSpecification().serialize();

		this._rules.push({
			accountSpecification,
			model: ruleTemplate.getRuleModel().serialize(),
			start: ruleTemplate.getStart().serialize(),
		});

		return this;
	}

	addSeriesSpecifications(seriesSpecifications) {
		this._seriesSpecifications.push(...seriesSpecifications);

		return this;
	}

	_makeReferencePeriods() {
		const report = this._forecast.getFirstReport();

		return [
			...report.getReferenceColumns(),
			report.getCurrentPeriodColumn(),
		].map((column) => column.getStart());
	}

	_makeForecastPeriods() {
		const report = this._forecast.getFirstReport();

		return report.getPredictedColumns().map((column) => column.getStart());
	}

	_makeSummaryValues() {
		const period =
			this._forecastPeriods.find((month) => month.isClosing()) ||
			this._forecastPeriods.at(-1);

		return this._forecast
			.getSummaryValueKeys()
			.map((key) => new ForecastSummaryValue({ key, period }));
	}

	_serializedProperties() {
		const properties = super._serializedProperties();

		properties.referencePeriods = this._referencePeriods;
		properties.forecastPeriods = this._forecastPeriods;
		properties.rules = this._rules;
		properties.seriesSpecifications = this._seriesSpecifications;
		properties.summaryValues = this._summaryValues;
		properties.description = this._description;
		properties.ignoredReportIds = this._ignoredReportIds;

		return properties;
	}
}
